/** Все пути приложения. Строки маршрутов в компонентах не пишутся. */
export const ROUTES = {
  /** Экраны 03–04 — каталог и его загрузка. */
  CATALOG: '/',
  /** Экран 05 — сертификат партнёра, выбор номинала (шаг 1 из 3). */
  BRAND: '/brands/:brandId',
  /** Экран 06 — получатель и поздравление (шаг 2 из 3). */
  CHECKOUT: '/checkout/:productId',
  /** Экран 07 — оплата: Click / Payme и «Оплатить» (шаг 3 из 3). */
  PAY: '/checkout/:productId/pay',
  /** Ожидание вебхука и исходы оплаты: 08 (прошла), 33 (не прошла), отмена и «время вышло». */
  ORDER: '/orders/:orderId',
  /**
   * Вход по карточке в чате и кнопкам бота получателю (`startapp=s_<token>`,
   * BE-53): экран 09 «Открытие подарка» или, по `view`, сразу решение,
   * ваучер, «не найден» (БП-7).
   */
  GIFT_SEND: '/open/:token',
  /**
   * Вход в подарок по id (`startapp=g_<id>`, «Мои подарки»): только свой
   * подарок — маршрутизация по `view` на решение, ваучер или состояние.
   */
  GIFT: '/gifts/:giftId',
  /** Экраны 10, 13, 14 — «Принять или отказаться». Повторный вход в своё `OPENED` — сразу сюда. */
  GIFT_DECISION: '/gifts/:giftId/decision',
  /** Экраны 15–19 — ваучер получателя. Повторный вход в своё `CLAIMED` — сразу сюда, без коробки. */
  VOUCHER: '/gifts/:giftId/voucher',
  /** Экраны 11–12 — «Где принимают» и выбор навигатора; «назад» — туда, откуда открыли. */
  GIFT_LOCATIONS: '/gifts/:giftId/locations',
  /** Экран 01 — онбординг: один раз при первом запуске без подарка (флаг `onboarded`). */
  ONBOARDING: '/welcome',
  /** Экран 20 — профиль. Вход — аватар в шапке каталога. */
  PROFILE: '/profile',
  /**
   * Экран 21 — «Мои подарки»: вкладки «Полученные» / «Подарить» (`?tab=`).
   * Вход — профиль и ссылка «Отправлю позже» на «Оплата прошла» (с `?gift=` —
   * купленный подарок подсвечен в списке).
   */
  MY_GIFTS: '/my-gifts',
  /** Экран 30 — «История заказов»: все купленные подарки с фильтром по статусу (`?filter=`). Вход — профиль. */
  HISTORY: '/history',
  /** Экран 29 — язык (uz / ru / en). Вход — профиль и «Оформление». */
  LANGUAGE: '/language',
  /**
   * Экран 31 — «Оформление»: тема и ячейка «Язык». Вход — пункт «Настройки» в
   * меню ⋮ Mini App (SettingsButton Telegram) и строка профиля; вне Telegram —
   * дев-оверлей или этот адрес.
   */
  SETTINGS: '/settings',
  /**
   * Поддержка — форма обращения (`POST /app/tickets`, БП-12). С экрана подарка
   * или заказа — с `?gift=<id>`: подарок привязывается к обращению.
   */
  SUPPORT: '/support',
  /** Мои обращения — список (`GET /app/tickets`). */
  SUPPORT_TICKETS: '/support/tickets',
  /** Обращение с перепиской и ответом (`GET /app/tickets/{id}`). */
  SUPPORT_TICKET: '/support/tickets/:ticketId',
  /**
   * Экраны 22–27 — сторис «Как отправить подарок». Открываются поверх экрана,
   * откуда их позвали (каталог, «Оплата прошла»); «назад», крестик и «Понятно»
   * возвращают туда же.
   */
  STORIES: '/how-to-send',
  NOT_FOUND: '*',
} as const

/** Параметры адреса экрана оплаты. */
export type PayPathParams = {
  /** Повтор оплаты уже созданного заказа — без нового заказа. */
  orderId?: string | null
  /** Предвыбранный агрегатор: «Другой способ оплаты» с экрана ошибки. */
  provider?: string | null
}

/**
 * Тема обращения — `TicketKind` бэкенда: от неё считаются сроки ответа (SLA,
 * БП-12). С экрана подарка или заказа приходит предвыбранной.
 */
export type SupportTopic = 'PAYMENT_ISSUE' | 'VOUCHER_NOT_ACCEPTED' | 'REFUND_REQUEST' | 'GENERAL'

/** Параметры адреса поддержки. */
export type SupportPathParams = {
  /** Подарок, с экрана которого пишут: привязывается к обращению. */
  giftId?: string | null
  topic?: SupportTopic | null
  /**
   * Номер заказа `OR-…` (BE-60) — с экрана оплаты: уходит в тему обращения
   * (Н-TMA4-10). Привязки к заказу у обращения нет — только к подарку.
   */
  orderNumber?: string | null
}

/** Вкладка «Моих подарков» (экран 21). */
export type MyGiftsTab = 'received' | 'give'

/**
 * Отметка в `location.state`: подарок открыт из «Моих подарков», а не по
 * ссылке из чата. Тогда у экрана решения (10) есть «Назад» — обратно в список
 * (решение 03.11 по Н-TMA5-5); по `start_param` — экран-момент без «Назад».
 */
export const FROM_MY_GIFTS_STATE = { fromMyGifts: true } as const

export const isFromMyGifts = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && (state as { fromMyGifts?: unknown }).fromMyGifts === true

/** Фильтр «Истории» (экран 30): «Истёкших» нет — подарок бессрочный (Р-5). */
export type HistoryFilter = 'all' | 'active' | 'redeemed' | 'refunded'

/** Параметры адреса «Моих подарков». */
export type MyGiftsPathParams = {
  tab?: MyGiftsTab | null
  /** Подарок, который подсветить в списке: только что купленный. */
  giftId?: string | null
}

/** Адреса с параметрами — для `navigate` и ссылок. */
export const buildPath = {
  /** `productId` — выбранный номинал: «назад» из чекаута возвращает выбор. */
  brand: (brandId: string, productId?: string): string =>
    `/brands/${encodeURIComponent(brandId)}${productId ? `?product=${encodeURIComponent(productId)}` : ''}`,
  checkout: (productId: string): string => `/checkout/${encodeURIComponent(productId)}`,
  pay: (productId: string, { orderId, provider }: PayPathParams = {}): string => {
    const search = new URLSearchParams()

    if (orderId) {
      search.set('order', orderId)
    }

    if (provider) {
      search.set('provider', provider)
    }

    const query = search.toString()

    return `/checkout/${encodeURIComponent(productId)}/pay${query ? `?${query}` : ''}`
  },
  order: (orderId: string): string => `/orders/${encodeURIComponent(orderId)}`,
  giftSend: (token: string): string => `/open/${encodeURIComponent(token)}`,
  gift: (giftId: string): string => `/gifts/${encodeURIComponent(giftId)}`,
  giftDecision: (giftId: string): string => `/gifts/${encodeURIComponent(giftId)}/decision`,
  voucher: (giftId: string): string => `/gifts/${encodeURIComponent(giftId)}/voucher`,
  giftLocations: (giftId: string): string => `/gifts/${encodeURIComponent(giftId)}/locations`,
  support: ({ giftId, topic, orderNumber }: SupportPathParams = {}): string => {
    const search = new URLSearchParams()

    if (giftId) {
      search.set('gift', giftId)
    }

    if (topic) {
      search.set('topic', topic)
    }

    if (orderNumber) {
      search.set('order', orderNumber)
    }

    const query = search.toString()

    return `/support${query ? `?${query}` : ''}`
  },
  supportTicket: (ticketId: string): string => `/support/tickets/${encodeURIComponent(ticketId)}`,
  myGifts: ({ tab, giftId }: MyGiftsPathParams = {}): string => {
    const search = new URLSearchParams()

    if (tab) {
      search.set('tab', tab)
    }

    if (giftId) {
      search.set('gift', giftId)
    }

    const query = search.toString()

    return `/my-gifts${query ? `?${query}` : ''}`
  },
}
